import axios from 'axios';

// Client for the fee-relayer's referrer dashboard API (apps/fee-relayer,
// /v3/fee). Every endpoint is scoped to the wallet that signs the request —
// there is no way to read another wallet's rows.

const BASE = process.env.NEXT_PUBLIC_MAYAN_FEE_RELAYER_URL;

export type WalletKind = 'evm' | 'solana';

// A signed message is a bearer credential until its timestamp falls outside
// the server's skew window, so it is held in memory only and re-signed on
// expiry rather than persisted anywhere.
export type FeeAuth = {
	address: string;
	signature: string;
	timestamp: number;
	kind: WalletKind;
};

// Must stay identical to buildAuthMessage in
// apps/fee-relayer/src/auth/wallet-signature.ts.
export function buildAuthMessage(address: string, timestamp: number): string {
	return `Mayan Fee Dashboard\naddress: ${address}\nts: ${timestamp}`;
}

export type Membership = {
	owner: string;
	commitment: string | null;
	bps: number;
	totalBps: number;
	chainKind: string;
};

export type Identity = {
	address: string;
	kind: string;
	memberships: Membership[];
	isSingleReferrer: boolean;
};

export type SkipReason =
	| 'TOKEN_NOT_REDEEMABLE'
	| 'CHAIN_NOT_SUPPORTED'
	| 'COMMITMENT_NOT_TRACKED'
	| 'INACTIVE'
	| 'ATA_CLOSED';

export type PositionRead =
	| { kind: 'evm'; owner: string; method: string; feeManager: string | null; token: string }
	| {
			kind: 'solana';
			owner: string;
			mint: string;
			mixedFeeAta: string | null;
			tokenProgram: string;
			isToken2022: boolean;
	  };

export type Position = {
	owner: string;
	chainId: number;
	chainKind: string;
	token: string;
	referrerKind: string;
	tokenKind: string | null;
	yourBps: number | null;
	totalBps: number | null;
	lastSeenSwapAt: string;
	lastRedeemedAt: string | null;
	lastCheckedAt: string | null;
	redeemable: boolean;
	reason: SkipReason | null;
	minAmount: { raw: string; decimals: number; human: string } | null;
	read: PositionRead;
};

export type PayoutRow = {
	kind: string;
	chainId: number;
	chainKind: string;
	token: string;
	txHash: string;
	blockTime: string;
	owner: string | null;
	commitment: string | null;
	totalAmount: string | null;
	yourAmount: string | null;
	yourBps: number;
	actor: string | null;
	decimals: number | null;
	symbol: string | null;
};

export type DepositRow = {
	chainId: number;
	chainKind: string;
	token: string;
	txHash: string;
	blockTime: string;
	owner: string | null;
	totalAmount: string | null;
	yourAmount: string | null;
	yourBps: number | null;
	decimals: number | null;
	symbol: string | null;
};

export type Paginated<T> = { data: T[]; total: number; limit: number; offset: number };
// History responses additionally carry the point the index starts from: the
// fee-relayer does not reach back to the FeeManager deployment, so an empty
// list means "nothing recorded yet", not "never paid".
export type PaginatedHistory<T> = Paginated<T> & { recordedSince: string };

function authHeaders(auth: FeeAuth): Record<string, string> {
	return {
		'X-Mayan-Address': auth.address,
		'X-Mayan-Signature': auth.signature,
		'X-Mayan-Timestamp': String(auth.timestamp),
	};
}

async function get<T>(path: string, auth: FeeAuth, params?: Record<string, unknown>): Promise<T> {
	if (!BASE) {
		throw new Error('NEXT_PUBLIC_MAYAN_FEE_RELAYER_URL is not set');
	}
	const { data } = await axios.get<T>(`${BASE}/v3/fee${path}`, {
		headers: authHeaders(auth),
		params,
	});
	return data;
}

export function getIdentity(auth: FeeAuth): Promise<Identity> {
	return get<Identity>('/identity', auth);
}

export function getPositions(
	auth: FeeAuth,
	params: { status?: 'redeemable' | 'skipped'; chainId?: number; limit?: number; offset?: number } = {},
): Promise<Paginated<Position>> {
	return get<Paginated<Position>>('/positions', auth, params);
}

export function getRedeems(
	auth: FeeAuth,
	params: { chainId?: number; kind?: string; limit?: number; offset?: number } = {},
): Promise<PaginatedHistory<PayoutRow>> {
	return get<PaginatedHistory<PayoutRow>>('/redeems', auth, params);
}

export function getDeposits(
	auth: FeeAuth,
	params: { chainId?: number; limit?: number; offset?: number } = {},
): Promise<PaginatedHistory<DepositRow>> {
	return get<PaginatedHistory<DepositRow>>('/deposits', auth, params);
}

export const SKIP_REASON_LABELS: Record<SkipReason, string> = {
	TOKEN_NOT_REDEEMABLE: 'Not an auto-redeemed token',
	CHAIN_NOT_SUPPORTED: 'Chain not served by the relayer',
	COMMITMENT_NOT_TRACKED: 'Commitment not registered',
	INACTIVE: 'No recent swaps on this pair',
	ATA_CLOSED: 'Fee account closed',
};

export const SKIP_REASON_HINTS: Record<SkipReason, string> = {
	TOKEN_NOT_REDEEMABLE:
		'Only USDC, USDT and ETH are redeemed automatically — anything else stays on the fee manager until it is worth a transaction.',
	CHAIN_NOT_SUPPORTED: 'The relayer has no configured wallet or fee manager for this chain.',
	COMMITMENT_NOT_TRACKED:
		'The referrer list behind this commitment has not been registered, and the redeem call cannot be built without it.',
	INACTIVE: 'The daily redeem only looks at pairs used recently; a new swap on this pair puts it back in scope.',
	ATA_CLOSED: 'The Solana fee account was closed after being emptied. It is recreated on the next fee.',
};
